import { Link, router, usePage } from '@inertiajs/react';
import { AppShell, Box, Burger, Button, Container, Group, Image, Modal, Stack, Text, useMantineTheme } from '@mantine/core';
import { useCounter, useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconLogin2, IconUserCircle } from '@tabler/icons-react';
import { useEffect } from 'react';

import goldframerLogo from '@/images/logo.svg';
import BookACallPopup from '@/pages/site/call-booking/BookACallPopup.jsx';
import SiteFooter from '@/pages/site/layouts/SiteFooter.jsx';

import popup from './popup-10usd.png';
import classes from './SiteNav.module.css';

export default function SiteNav({ showFooter = true, children }) {
  const auth = usePage().props?.auth;

  // show popup when mouse leavbes the page
  const [popupState, popupStateHandler] = useCounter(0);

  useEffect(() => {
    const hasShown = sessionStorage.getItem('popupShown');

    const popupHandler = e => {
      if (!hasShown && !e.toElement && !e.relatedTarget && !auth?.user && e.clientY < 1) {
        popupStateHandler.increment();
        sessionStorage.setItem('popupShown', 'true');
      }
    };

    window.addEventListener('mouseout', popupHandler);
    return () => window.removeEventListener('mouseout', popupHandler);
  }, [auth]);

  // Mantine AppShell
  const [opened, { toggle }] = useDisclosure();
  const collapseBreakpoint = 'md';
  const theme = useMantineTheme();

  // check mobile mantine way
  const isMobileS = useMediaQuery('(max-width: 374px)');

  // Close navbar if route changes
  useEffect(() => {
    return router.on('start', _event => {
      if (opened) {
        toggle();
      }
    });
  }, [opened]);

  const mpEnabled = import.meta.env?.VITE_MP_ENABLED;

  const links = (mobile = false) => (
    <>
      <Link href={route('site.art-sales')} className={classes.control}>
        ART SALES
      </Link>
      {/*<Link href={route('site.auctions')} className={classes.control}>*/}
      {/*  AUCTIONS*/}
      {/*</Link>*/}
      <Link href={route('site.how-it-works')} className={classes.control}>
        HOW IT WORKS
      </Link>
      <Link href={route('site.guide')} className={classes.control}>
        BEGINNER'S GUIDE
      </Link>
      {mpEnabled ? (
        <Link href={route('site.marketplace')}>
          <Box className={[classes.control]} pos={'relative'}>
            MARKETPLACE{' '}
            <Text hiddenFrom="md" fz={'10px'} c={'gf.7'} span>
              BETA
            </Text>
            <Text visibleFrom="md" fz={'10px'} c={'gf.7'} pos={'absolute'} bottom={0} right={12}>
              BETA
            </Text>
          </Box>
        </Link>
      ) : (
        <Box c={'gfdark.4'} style={{ cursor: 'default' }} data-disabled className={[classes.control]} pos={'relative'}>
          MARKETPLACE{' '}
          <Text hiddenFrom="md" fz={'10px'} c={'gf.7'} span>
            COMING SOON
          </Text>
          <Text visibleFrom="md" fz={'10px'} c={'gf.7'} pos={'absolute'} bottom={0} right={12}>
            COMING SOON
          </Text>
        </Box>
      )}
      <Link href={route('site.about')} className={classes.control}>
        ABOUT
      </Link>
      <a href="https://blog.goldframer.com" className={classes.control}>
        BLOG
      </a>
      {mobile ? (
        <>
          {!!auth?.user && (
            <>
              <Link href={route('site.earn')} className={classes.control}>
                EARN $100
              </Link>
              <Link href={route('site.account.wallet')} className={classes.control}>
                WALLET
              </Link>
            </>
          )}
        </>
      ) : null}
    </>
  );

  return (
    <AppShell
      classNames={{
        root: classes.root,
        main: showFooter ? classes.main : classes.mainNoFooter,
        footer: classes.footer,
      }}
      header={{ height: theme.other.headerHeight }}
      navbar={{ width: 300, breakpoint: collapseBreakpoint, collapsed: { desktop: true, mobile: !opened } }}
    >
      <AppShell.Header>
        <Container pr={0}>
          <Group h="60">
            <Burger opened={opened} onClick={toggle} hiddenFrom={collapseBreakpoint} size="sm" />
            <Group justify="space-between" style={{ flex: 1 }} wrap={'nowrap'} gap={0}>
              <Group>
                <Link href={'/'}>
                  <Image src={goldframerLogo} />
                </Link>

                <Group gap={0} visibleFrom={collapseBreakpoint}>
                  {links()}
                </Group>
              </Group>

              {/* Mobile Header */}
              <Group ml="xl" gap={0} hiddenFrom={collapseBreakpoint} wrap={'nowrap'}>
                {!!auth?.user && (
                  <Link href={route('site.account')} className={classes.control}>
                    <Group gap={'xs'} c="var(--color-gf)" wrap={'nowrap'}>
                      <IconUserCircle stroke={1.5} size={'1rem'} />{' '}
                      <Text c="var(--color-main)" size="inherit" hidden={isMobileS} lh={1} fz={'sm'}>
                        {auth?.user?.name}
                      </Text>
                    </Group>
                  </Link>
                )}
              </Group>

              {/* Header */}
              <Group ml="xl" gap={0} visibleFrom={collapseBreakpoint}>
                {!auth?.user && (
                  <>
                    <Link href={route('login')} className={classes.control}>
                      <Group gap={'xs'} c="var(--color-gf)" wrap={'nowrap'}>
                        <IconLogin2 />{' '}
                        <Text c="var(--color-main)" size="inherit">
                          LOGIN
                        </Text>
                      </Group>
                    </Link>
                    <Link href={route('register')} className={classes.control}>
                      <Text c="var(--color-gf)" size="inherit">
                        SIGN UP
                      </Text>
                    </Link>
                  </>
                )}

                {!!auth?.user && (
                  <>
                    <Button
                      component={Link}
                      href={route('site.earn')}
                      variant="outline"
                      color="yellow"
                      radius="xs"
                      c={'gfdark.9'}
                      mr={'sm'}
                      fw={500}
                      size="xs"
                    >
                      EARN $100
                    </Button>

                    <Link href={route('site.account.wallet')} className={classes.control}>
                      <Group gap={5} c="var(--color-gf)">
                        <Text c="var(--color-main)">WALLET</Text>
                      </Group>
                    </Link>

                    <Link href={route('site.account')} className={classes.control}>
                      <Group gap={5} c="var(--color-gf)">
                        <IconUserCircle stroke={1.5} size={'1rem'} />
                        <Text c="var(--color-main)">{auth?.user?.name}</Text>
                      </Group>
                    </Link>
                  </>
                )}
              </Group>
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      {/* Mobile Nav */}
      <AppShell.Navbar py="md" px={'lg'}>
        <Stack align="stretch" justify="space-between" h={'100%'} fz={'xl'}>
          <div>{links(true)}</div>
          <div>
            {!auth?.user && (
              <Group justify={'space-between'}>
                <Link href={route('register')} className={classes.control}>
                  <Text fz={'inherit'} c={'gf'}>
                    SIGN UP
                  </Text>
                </Link>
                <Link href={route('login')} className={classes.control}>
                  <Group gap={'xs'} c="var(--color-gf)">
                    <IconLogin2 />{' '}
                    <Text c="gfdark.9" fz="inherit">
                      LOGIN
                    </Text>
                  </Group>
                </Link>
              </Group>
            )}
            {!!auth?.roles?.includes('admin') && (
              <>
                <Link href="/panel" className={classes.control}>
                  PANEL
                </Link>
              </>
            )}
            {!!auth?.user && (
              <>
                <a href="/logout" className={classes.control}>
                  LOGOUT
                </a>
              </>
            )}
          </div>
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        {children}

        <Modal
          withCloseButton={false}
          padding={0}
          radius={'lg'}
          size={'auto'}
          opened={popupState === 1}
          onClose={popupStateHandler.increment}
          centered
        >
          <Link href={route('register')}>
            <Image src={popup} maw={700} />
          </Link>
        </Modal>

        {/* Book a Call */}
        {!opened && <BookACallPopup showFooter={showFooter} />}
      </AppShell.Main>

      {showFooter && (
        <AppShell.Footer withBorder={false}>
          <SiteFooter />
        </AppShell.Footer>
      )}
    </AppShell>
  );
}
